import { Link } from "react-router-dom";
import { ArrowRight, Route } from "lucide-react";
import { cn } from "@/lib/utils";

type DecisionPathLink = {
  title: string;
  description: string;
  href: string;
  label?: string;
  badge?: string;
};

type DecisionPathLinksProps = {
  eyebrow?: string;
  title: string;
  description: string;
  links: DecisionPathLink[];
  className?: string;
};

function LinkContent({ item }: { item: DecisionPathLink }) {
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {item.badge && (
            <span className="mb-3 inline-flex rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
              {item.badge}
            </span>
          )}
          <h3 className="text-base font-semibold leading-tight text-foreground">{item.title}</h3>
        </div>
        <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-primary transition-transform group-hover:translate-x-0.5" />
      </div>
      <p className="mt-2 line-clamp-3 text-sm leading-6 text-muted-foreground">{item.description}</p>
      <span className="mt-4 inline-flex text-sm font-medium text-primary">
        {item.label || "Gå vidare"}
      </span>
    </>
  );
}

export function DecisionPathLinks({
  eyebrow = "Nästa steg",
  title,
  description,
  links,
  className,
}: DecisionPathLinksProps) {
  return (
    <section className={cn("border-y border-border/60 bg-background py-8 md:py-12", className)}>
      <div className="container max-w-6xl">
        <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              <Route className="h-4 w-4" />
              {eyebrow}
            </div>
            <h2 className="text-2xl font-bold tracking-tight md:text-3xl">{title}</h2>
            <p className="text-muted-foreground">{description}</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {links.map((item) => {
              const itemClassName =
                "group flex min-h-[170px] flex-col rounded-lg border border-border/70 bg-card p-4 text-left transition-colors hover:border-primary/50 hover:bg-primary/5";

              if (item.href.startsWith("#")) {
                return (
                  <a key={item.href} href={item.href} className={itemClassName}>
                    <LinkContent item={item} />
                  </a>
                );
              }

              return (
                <Link key={item.href} to={item.href} className={itemClassName}>
                  <LinkContent item={item} />
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
